import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { C2Button } from '../components/c2';
import AuthShell from '../components/auth/AuthShell';
import AuthInput from '../components/auth/Input';
import { api, type ApiError } from '../lib/api';

export default function ForgotPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email) return;
    setLoading(true);
    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (err) {
      // The endpoint usually returns 200 even for unknown emails (no enumeration),
      // so any error here is unexpected.
      setError((err as ApiError).message || 'Could not send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      {!sent ? (
        <>
          <h2
            className="font-display font-extrabold text-ink m-0 mb-2.5"
            style={{ fontSize: 30, letterSpacing: '-0.02em' }}
          >
            Reset your password
          </h2>
          <p className="text-[15px] text-ink-soft m-0 mb-6 leading-[1.55]">
            Enter your email and we'll send you a link to set a new password.
          </p>
          <form onSubmit={handleSubmit}>
            <AuthInput
              label="Email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus
            />
            {error && (
              <p className="text-[13px] mb-3" style={{ color: 'var(--color-peach-deep)' }}>{error}</p>
            )}
            <C2Button variant="accent" size="lg" className="w-full mt-2" type="submit" disabled={loading}>
              {loading ? '…' : 'Send reset link'}
            </C2Button>
          </form>
          <Link
            to="/login"
            className="w-full bg-transparent border-0 cursor-pointer text-[13px] text-ink-soft text-center block no-underline"
            style={{ padding: '20px 0 0' }}
          >
            ← Back to sign in
          </Link>
        </>
      ) : (
        <div className="text-center">
          <div
            className="w-[72px] h-[72px] rounded-full mx-auto mb-5 flex items-center justify-center"
            style={{ background: 'var(--accent-soft)', color: 'var(--accent-deep)', fontSize: 32 }}
          >
            ✉
          </div>
          <h2
            className="font-display font-extrabold text-ink m-0 mb-2.5"
            style={{ fontSize: 28, letterSpacing: '-0.02em' }}
          >
            Check your email
          </h2>
          <p className="text-[15px] text-ink-soft m-0 mb-6 leading-[1.55]">
            We sent a reset link to <strong className="text-ink">{email}</strong>.
          </p>
          <C2Button variant="outline" onClick={() => navigate('/login')}>
            Back to sign in
          </C2Button>
        </div>
      )}
    </AuthShell>
  );
}
